import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  VStack, 
  Heading, 
  Text, 
  useToast, 
  InputGroup,
  InputRightElement,
  IconButton,
  Flex,
  Alert,
  AlertIcon,
  Progress,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import api from '../services/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    // Validate that we have both token and email
    if (!token || !email) {
      setTokenValid(false);
      toast({
        title: 'Invalid Reset Link',
        description: 'This password reset link is invalid or incomplete. Please request a new one.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
  }, [token, email, toast]);

  // Password validation
  const getPasswordStrength = () => {
    if (!password) return { score: 0, label: '', color: 'gray' };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 2) return { score: 25, label: 'Weak', color: 'red' };
    if (score === 3) return { score: 50, label: 'Fair', color: 'orange' };
    if (score === 4) return { score: 75, label: 'Good', color: 'yellow' };
    return { score: 100, label: 'Strong', color: 'green' };
  };

  const passwordStrength = getPasswordStrength();
  const passwordsMatch = password === confirmPassword && password !== '';
  const isValidPassword = password.length >= 8 && passwordsMatch;

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[a-z]/.test(password) && /[A-Z]/.test(password), text: 'Uppercase and lowercase letters' },
    { met: /\d/.test(password), text: 'At least one number' },
    { met: /[^a-zA-Z0-9]/.test(password), text: 'At least one special character (recommended)' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidPassword) {
      toast({
        title: 'Invalid Password',
        description: 'Please ensure your password meets all requirements and passwords match.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { data } = await api.post(
        `/api/auth/reset-password?token=${encodeURIComponent(token)}`,
        { 
          email: email.toLowerCase().trim(),
          password: password
        }
      );
      
      toast({
        title: 'Password Reset Successful',
        description: data.message || 'Your password has been reset. You can now log in with your new password.',
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Password reset successfully. Please log in with your new password.' }
        });
      }, 2000);
      
    } catch (error) {
      console.error('Password reset failed:', error);
      
      const errorMessage = error.response?.data?.detail || 'Failed to reset password';
      
      // Check if token expired
      if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        toast({
          title: 'Reset Link Expired',
          description: 'This reset link has expired or is invalid. Please request a new password reset.',
          status: 'error',
          duration: 7000,
          isClosable: true,
        });
        setTokenValid(false);
      } else {
        toast({
          title: 'Reset Failed',
          description: errorMessage,
          status: 'error',
          duration: 7000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <Flex align="center" justify="center" minH="80vh">
        <Box 
          maxW="md" 
          w="100%" 
          p={8} 
          borderWidth={1} 
          borderRadius="lg" 
          boxShadow="lg"
          bg="#1a1a22"
          borderColor="rgba(80, 80, 80, 0.6)"
        >
          <VStack spacing={6}>
            <Box mb={2}>
              <img 
                src="/gillyreach-favicon.png" 
                alt="GillyReach" 
                style={{ height: '60px', width: 'auto', margin: '0 auto' }}
              />
            </Box>
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              borderRadius="md"
              bg="rgba(245, 101, 101, 0.1)"
              border="1px solid"
              borderColor="rgba(245, 101, 101, 0.3)"
              py={6}
            >
              <AlertIcon boxSize="40px" mr={0} color="red.400" />
              <Heading size="md" mt={4} mb={2} color="#e0e0e8">
                Invalid or Expired Link
              </Heading>
              <Text color="gray.400" maxWidth="sm">
                This password reset link is invalid or has expired. 
                Reset links are only valid for 5 minutes.
              </Text>
            </Alert>
            
            <Link to="/forgot-password" style={{ width: '100%' }}>
              <Button
                variant="solid"
                bg="rgba(65, 105, 225, 0.8)"
                color="white"
                _hover={{ bg: "rgba(65, 105, 225, 0.9)" }}
                width="full"
              >
                Request New Reset Link
              </Button>
            </Link>
            
            <Link to="/login" style={{ width: '100%' }}>
              <Button
                variant="ghost"
                width="full"
                color="rgba(65, 105, 225, 0.8)"
                _hover={{ bg: "rgba(65, 105, 225, 0.1)" }}
              >
                Back to Login
              </Button>
            </Link>
          </VStack>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex align="center" justify="center" minH="80vh" py={8}>
      <Box 
        maxW="md" 
        w="100%" 
        p={8} 
        borderWidth={1} 
        borderRadius="lg" 
        boxShadow="lg"
        bg="#1a1a22"
        borderColor="rgba(80, 80, 80, 0.6)"
      >
        <VStack spacing={6}>
          <Box mb={2}>
            <img 
              src="/gillyreach-favicon.png" 
              alt="GillyReach" 
              style={{ height: '60px', width: 'auto', margin: '0 auto' }}
            />
          </Box>
          <Heading color="#e0e0e8" size="lg">Set New Password</Heading>
          
          <Text color="gray.400" textAlign="center">
            Enter a new password for <strong style={{ color: '#e0e0e8' }}>{email}</strong>
          </Text>
          
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <VStack spacing={4} align="stretch">
              <FormControl id="password" isRequired>
                <FormLabel color="#e0e0e8">New Password</FormLabel>
                <InputGroup>
                  <Input 
                    type={showPassword ? 'text' : 'password'} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    bg="#1a1a22"
                    borderColor="gray.600"
                    color="#e0e0e8"
                    _hover={{ borderColor: "gray.500" }}
                    _focus={{ borderColor: "rgba(65, 105, 225, 0.8)" }}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      variant="ghost"
                      color="gray.400"
                      onClick={() => setShowPassword(!showPassword)}
                      _hover={{ bg: "rgba(65, 105, 225, 0.1)" }}
                    />
                  </InputRightElement>
                </InputGroup>
                
                {password && (
                  <Box mt={2}>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="xs" color="gray.400">
                        Password strength:
                      </Text>
                      <Text fontSize="xs" color={`${passwordStrength.color}.400`} fontWeight="bold">
                        {passwordStrength.label}
                      </Text>
                    </Flex>
                    <Progress 
                      value={passwordStrength.score} 
                      size="sm" 
                      colorScheme={passwordStrength.color}
                      borderRadius="full"
                      bg="gray.700"
                    />
                  </Box>
                )}
              </FormControl>
              
              <FormControl id="confirmPassword" isRequired>
                <FormLabel color="#e0e0e8">Confirm New Password</FormLabel>
                <InputGroup>
                  <Input 
                    type={showConfirmPassword ? 'text' : 'password'} 
                    value={confirmPassword} 
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    bg="#1a1a22"
                    borderColor={
                      confirmPassword === '' ? "gray.600" : 
                      passwordsMatch ? "green.500" : "red.500"
                    }
                    color="#e0e0e8"
                    _hover={{ borderColor: "gray.500" }}
                    _focus={{ borderColor: "rgba(65, 105, 225, 0.8)" }}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                      size="sm"
                      variant="ghost"
                      color="gray.400"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      _hover={{ bg: "rgba(65, 105, 225, 0.1)" }}
                    />
                  </InputRightElement>
                </InputGroup>
                
                {confirmPassword && (
                  <Text 
                    fontSize="xs" 
                    color={passwordsMatch ? "green.400" : "red.400"}
                    mt={1}
                  >
                    {passwordsMatch ? "✓ Passwords match" : "✗ Passwords do not match"}
                  </Text>
                )}
              </FormControl>
              
              {password && (
                <Box 
                  p={3} 
                  bg="rgba(65, 105, 225, 0.05)" 
                  borderRadius="md"
                  border="1px solid"
                  borderColor="rgba(65, 105, 225, 0.2)"
                >
                  <Text fontSize="sm" color="gray.300" mb={2} fontWeight="semibold">
                    Password Requirements:
                  </Text>
                  <List spacing={1}>
                    {passwordRequirements.map((req, index) => (
                      <ListItem key={index} fontSize="xs" color={req.met ? "green.400" : "gray.500"}>
                        <ListIcon 
                          as={req.met ? CheckCircleIcon : WarningIcon} 
                          color={req.met ? "green.400" : "gray.500"}
                        />
                        {req.text}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              
              <Button 
                type="submit" 
                variant="solid"
                bg="rgba(65, 105, 225, 0.8)"
                color="white"
                _hover={{ bg: "rgba(65, 105, 225, 0.9)" }}
                width="full" 
                mt={4} 
                isLoading={isLoading}
                isDisabled={!isValidPassword}
              >
                Reset Password
              </Button>
            </VStack>
          </form>
          
          <Link to="/login" style={{ width: '100%' }}>
            <Button
              variant="ghost"
              width="full"
              color="rgba(65, 105, 225, 0.8)"
              _hover={{ bg: "rgba(65, 105, 225, 0.1)" }}
            >
              Back to Login
            </Button>
          </Link>
        </VStack>
      </Box>
    </Flex>
  );
};

export default ResetPasswordPage;

